import { Component, effect, inject, input, signal, ViewChild, ElementRef, Output, EventEmitter, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GlobalDateFormatPipe } from '../../../../../../shared/pipes/global-date-format.pipe';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { ButtonComponent } from '../../../../../../shared/components/ui/button/button.component';
import { MicroServiceHttpService } from '../../../../../../core/services/microServiceHttp.service';
import { finalize } from 'rxjs/operators';
import { toast } from 'ngx-sonner';

@Component({
  selector: 'app-knowledge-base',
  standalone: true,
  imports: [CommonModule, IconComponentV2, ButtonComponent, GlobalDateFormatPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './knowledge-base.component.html'
})
export class KnowledgeBaseComponent {
  private readonly http = inject(MicroServiceHttpService);

  botId = input<number | null>(null);
  botName = input<string | null>(null);
  nodeId = input<number | null>(null);

  @Output() documentUploaded = new EventEmitter<{ collectionId: number }>();

  documents = signal<any[]>([]);
  loading = signal(false);
  isDeletingIds = signal<string[]>([]);
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  uploadModal = viewChild<ElementRef<HTMLDialogElement>>('uploadModal');
  
  selectedFiles = signal<File[]>([]);
  isUploading = signal(false);

  constructor() {
    effect(() => {
      const bid = this.botId();
      const nid = this.nodeId();
      if (bid) {
        this.loadDocuments(bid, nid);
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const modal = this.uploadModal()?.nativeElement;
      if (modal && !modal.open && this.selectedFiles().length > 0) {
        modal.showModal();
      }
    });
  }

  loadDocuments(chatbotId: number, workflowNodeId: number | null): void {
    this.loading.set(true);
    let url = `knowledge-base/documents?chatbotId=${chatbotId}`;
    if (workflowNodeId != null) {
      url += `&workflowNodeId=${workflowNodeId}`;
    }
    
    this.http.get<any>('whitelabel', url, { showLoader: false, showToast: false })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          this.documents.set(Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []);
        },
        error: () => {
          this.documents.set([]);
        }
      });
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  extractFilename(documentLink: string | null): string {
    if (!documentLink) return 'Unknown File';
    return documentLink.split('/').pop() || 'Unknown File';
  }

  deleteDocument(itemId: number, documentId: string): void {
    if (!itemId) return;
    this.isDeletingIds.update(ids => [...ids, documentId]);
    
    this.http.delete<any>('whitelabel', `knowledge-base/document/${itemId}`, {
      showLoader: false,
      showToast: true,
      successMessage: 'Document deleted successfully'
    }).pipe(
      finalize(() => {
        this.isDeletingIds.update(ids => ids.filter(id => id !== documentId));
      })
    ).subscribe({
      next: () => {
        const bid = this.botId();
        const nid = this.nodeId();
        if (bid) {
          this.loadDocuments(bid, nid);
        }
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const allowedExt = ['.pdf', '.docx', '.txt'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      const currentFiles = this.selectedFiles();
      let newFiles = Array.from(input.files).filter(file => {
        const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
        const isValidExt = allowedExt.includes(ext);
        const isValidSize = file.size <= maxSize;
        const isDuplicate = currentFiles.some(
          existing => existing.name === file.name && existing.size === file.size
        );

        if (isDuplicate) {
          toast.error(`File "${file.name}" is already added.`);
          return false;
        }
        if (!isValidExt) {
          toast.error(`File "${file.name}" has an unsupported format. (PDF, DOCX, TXT only)`);
          return false;
        }
        if (!isValidSize) {
          toast.error(`File "${file.name}" is larger than the 10MB limit.`);
          return false;
        }
        return true;
      });
      
      const totalAllowed = Math.max(0, 5 - currentFiles.length);
      if (newFiles.length > totalAllowed) {
        toast.error('You can only upload a maximum of 5 files at a time.');
        newFiles = newFiles.slice(0, totalAllowed);
      }
      
      this.selectedFiles.update(files => [...files, ...newFiles]);
    }
    input.value = '';
  }

  triggerFileInput(): void {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  removeSelectedFile(index: number): void {
    this.selectedFiles.update(files => files.filter((_, i) => i !== index));
  }

  clearSelectedFiles(): void {
    this.selectedFiles.set([]);
  }

  uploadFiles(): void {
    const files = this.selectedFiles();
    if (files.length === 0) return;

    const bid = this.botId();
    if (bid == null) return;

    const botName = this.botName();
    if (botName == null) return;

    this.isUploading.set(true);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    formData.append('name', `${botName} - knowledge base`);

    const nid = this.nodeId();
    if (nid != null) {
      formData.append('workflowNodeId', nid.toString());
    } else {
      formData.append('chatbotId', bid.toString());
    }

    this.http.post<any>('whitelabel', 'knowledge-base/collection', formData, { showLoader: false, showToast: true, successMessage: 'Documents uploaded successfully' })
      .pipe(finalize(() => this.isUploading.set(false)))
      .subscribe({
        next: (res) => {
          this.clearSelectedFiles();
          const cId = res?.collectionId || res?.data?.collectionId;
          if (cId) {
            this.documentUploaded.emit({ collectionId: cId });
          }
        }
      });
  }
}
